import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type CardSettingDialogProps = {
  children: React.ReactNode;
  contentId: string;
  icon: React.ReactNode;
  label: string;
  onClose: () => void;
  onOpen: () => void;
  open: boolean;
  ringed?: boolean;
  summary: string;
};

/** A compact setting in the pane, with its full controls in a focused dialog. */
export default function CardSettingDialog({
  children,
  contentId,
  icon,
  label,
  onClose,
  onOpen,
  open,
  ringed,
  summary,
}: CardSettingDialogProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box sx={{ mb: 1 }}>
      <ButtonBase
        aria-controls={open ? contentId : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={onOpen}
        sx={{
          alignItems: 'center',
          borderLeft: `3px solid ${ringed ? tokens.coralAccent : 'transparent'}`,
          borderRadius: 1,
          bgcolor: theme.palette.action.hover,
          display: 'flex',
          gap: 1.5,
          minHeight: 64,
          px: 1.25,
          py: 1.25,
          textAlign: 'left',
          width: '100%',
          '&:hover': { bgcolor: theme.palette.action.selected },
          '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
        }}
      >
        <Box sx={{ alignItems: 'center', color: tokens.accent, display: 'inline-flex', flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{label}</Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4, mt: 0.25, overflowWrap: 'anywhere' }}>
            {summary}
          </Typography>
        </Box>
        <ArrowForwardIosIcon sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 14 }} />
      </ButtonBase>
      <Dialog
        aria-labelledby={`${contentId}-title`}
        fullWidth
        maxWidth="sm"
        onClose={onClose}
        open={open}
        PaperProps={{ 'data-lens-card-settings-dialog': 'true' }}
        scroll="paper"
      >
        <DialogTitle id={`${contentId}-title`} sx={{ pb: 1 }}>
          {label}
        </DialogTitle>
        <DialogContent id={contentId} sx={{ pt: '12px !important' }}>
          {children}
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, py: 2 }}>
          <Typography color="text.secondary" sx={{ flex: 1, fontSize: 12 }}>
            Save chart to keep these changes.
          </Typography>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
